import { router } from 'expo-router';
import { Pressable, StyleSheet, View } from 'react-native';

import { colors, fonts, fontSizes, spacing } from '@/constants/theme';
import { formatRelative } from '@/lib/relativeTime';
import {
  EARNING_KIND,
  EARNING_STATUS,
  type Earning,
  type EarningKind,
  type EarningStatus,
} from '@/types/earning';

import { Avatar } from './Avatar';
import { BrandLogo } from './BrandLogo';
import { Inline } from './Inline';
import type { PillTone } from './Pill';
import { Text } from './Text';

// Slim row primitive — lives inside a parent Card with hairline dividers
// between siblings. Status renders as a colored uppercase micro-label
// (no pill chrome), matching handoff `home.jsx:228–254`. Used in Home
// Recent Activity AND the dedicated /earnings list (a single shared
// row primitive across both surfaces — visual consistency wins).
//
// User-facing event verbs ("Earned" / "Cashed out" / "Refunded") instead
// of backend lifecycle bucket names ("Available" / "Cashed Out" / "Voided")
// — the user thinks "I earned this," not "this is in the Available state."
//
// Every row is an earning the user RECEIVED, so the amount always renders
// "+$X" — this is an earnings feed, not a balance ledger. Withdrawals belong
// in cashout history, where a real cashout is a single negative event; an
// earning whose status is `cashed_out` is still money the user earned, so it
// keeps its "+". Status is conveyed by the color of the "+" (and the a11y
// label), never by flipping the sign:
//   Earned     → +$X  (electric-green pop — money in)
//   Cashed out → +$X  (muted deep-green — earned, already withdrawn)
//   Refunded   → +$X  (coral — reversed by the brand)
export const ACTIVITY_STATUS_COLOR: Record<EarningStatus, string> = {
  [EARNING_STATUS.Available]: colors.deepGreen,
  [EARNING_STATUS.CashedOut]: colors.deepGreen,
  [EARNING_STATUS.Voided]: '#7A1F1F',
};

// Color of the "+" + amount glyphs. Available pops the "+" in Electric
// Green to mark the credit moment; everything else uses the status color
// so refunded reads as coral and cashed-out reads as muted-deep-green.
export const ACTIVITY_STATUS_SIGN_COLOR: Record<EarningStatus, string> = {
  [EARNING_STATUS.Available]: colors.electricGreen,
  [EARNING_STATUS.CashedOut]: colors.deepGreen,
  [EARNING_STATUS.Voided]: '#7A1F1F',
};

export const ACTIVITY_STATUS_AMOUNT_COLOR: Record<EarningStatus, string> = {
  [EARNING_STATUS.Available]: colors.deepGreen,
  [EARNING_STATUS.CashedOut]: colors.deepGreen,
  [EARNING_STATUS.Voided]: '#7A1F1F',
};

export const ACTIVITY_STATUS_LABEL: Record<EarningStatus, string> = {
  [EARNING_STATUS.Available]: 'Earned',
  [EARNING_STATUS.CashedOut]: 'Cashed out',
  [EARNING_STATUS.Voided]: 'Refunded',
};

export const ACTIVITY_KIND_LABEL: Record<EarningKind, string> = {
  [EARNING_KIND.Direct]: 'Direct',
  [EARNING_KIND.Referral]: 'Referral',
  [EARNING_KIND.InviteBonus]: 'Invite bonus',
  [EARNING_KIND.InviteWelcome]: 'Welcome bonus',
};

// Pill-chrome tone mappings used by the detail screen meta panel
// (where actual <Pill> components render the status + kind). Live here
// alongside the row labels so all earning-domain visual mappings have
// one home.
export const ACTIVITY_STATUS_PILL_TONE: Record<EarningStatus, PillTone> = {
  [EARNING_STATUS.Available]: 'greenSoft',
  [EARNING_STATUS.CashedOut]: 'green',
  [EARNING_STATUS.Voided]: 'coral',
};

export const ACTIVITY_KIND_PILL_TONE: Record<EarningKind, PillTone> = {
  [EARNING_KIND.Direct]: 'dark',
  [EARNING_KIND.Referral]: 'blue',
  [EARNING_KIND.InviteBonus]: 'greenSoft',
  [EARNING_KIND.InviteWelcome]: 'greenSoft',
};

function formatAmount(cents: number): string {
  // "+$50" / "+$1.25" — never a trailing .00. Sign always positive (the
  // earning record itself is what the user got; voided earnings still
  // render as "+$X — Voided" because that's the original event amount).
  const dollars = Math.floor(cents / 100);
  const remainder = cents % 100;
  if (remainder === 0) return `${dollars}`;
  return (cents / 100).toFixed(2);
}

export type ActivityRowProps = {
  earning: Earning;
  /** Hairline divider below this row. Default true; pass false on the last row. */
  divider?: boolean;
  /** Override the default `router.push('/earnings/' + id)` behavior, useful for tests. */
  onPress?: () => void;
  testID?: string;
};

export function ActivityRow({ earning, divider = true, onPress, testID }: ActivityRowProps) {
  const initials = earning.deal.brand_name.slice(0, 2).toUpperCase();
  const statusLabel = ACTIVITY_STATUS_LABEL[earning.status];
  const kindLabel = ACTIVITY_KIND_LABEL[earning.kind];
  // Referral + invite_bonus rows are "someone else earned this for you" —
  // lead with the friend's name (person = the action's user on every kind,
  // so invite_bonus carries it too). invite_welcome is the user's own money
  // and reads like a direct row. The deal is named by its offer (SoFi
  // Invest), never the shared brand: three SoFi deals share "SoFi".
  const earnedByFriend =
    earning.kind === EARNING_KIND.Referral || earning.kind === EARNING_KIND.InviteBonus;
  const personName = earnedByFriend ? earning.person?.public_name : undefined;
  const title = personName ?? earning.deal.display_name;
  const subtitle = earnedByFriend
    ? [earning.deal.display_name, formatRelative(earning.created_at)].join(' · ')
    : formatRelative(earning.created_at);
  const signColor = ACTIVITY_STATUS_SIGN_COLOR[earning.status];
  const amountColor = ACTIVITY_STATUS_AMOUNT_COLOR[earning.status];

  const handlePress = onPress ?? (() => router.push(`/earnings/${earning.id}`));

  const a11yLabel = [
    earning.deal.display_name,
    kindLabel,
    personName,
    `+$${(earning.amount_cents / 100).toFixed(2)}`,
    statusLabel,
  ].filter(Boolean).join(', ');

  return (
    <Pressable
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={a11yLabel}
      style={({ pressed }) => [
        styles.row,
        divider && styles.rowDivider,
        pressed && styles.rowPressed,
      ]}
      testID={testID}
    >
      <BrandLogo
        url={earning.deal.brand_logo_url}
        size={36}
        radius={10}
        fallback={<Avatar initials={initials} size={36} tone="green" />}
      />
      <View style={styles.body}>
        <Text variant="bodyBold" color="deepGreen" numberOfLines={1}>
          {title}
        </Text>
        <Text variant="caption" color="textMuted" numberOfLines={1} style={styles.caption}>
          {subtitle}
        </Text>
        <Inline gap="xs" align="center" style={styles.chipRow}>
          <Text style={styles.kindChip}>{kindLabel}</Text>
        </Inline>
      </View>
      <View style={styles.right}>
        <View style={styles.amountRow}>
          <Text style={[styles.amountSign, { color: signColor }]}>+$</Text>
          <Text style={[styles.amount, { color: amountColor }]}>
            {formatAmount(earning.amount_cents)}
          </Text>
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 14,
    paddingHorizontal: spacing.base,
  },
  rowDivider: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
  },
  rowPressed: {
    opacity: 0.7,
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  caption: {
    marginTop: 2,
  },
  chipRow: {
    marginTop: spacing.sm,
  },
  kindChip: {
    overflow: 'hidden',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 3,
    backgroundColor: colors.brightGreenSoft,
    color: colors.deepGreen,
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.xs,
    lineHeight: 16,
  },
  right: {
    alignItems: 'flex-end',
    minWidth: 78,
  },
  amountRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  amountSign: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.lg,
  },
  amount: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.xl,
    letterSpacing: 0,
  },
});
